import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Users, Calendar, Palette, Mountain, Store, MessageCircle, Activity } from 'lucide-react';
import { PROJECT_CREATOR } from '../constants/contacts';

const Landing = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="container-max py-20">
          <div className="text-center max-w-4xl mx-auto">
            <div className="mb-8">
              <img 
                src="/t-alento_logo.png" 
                alt="T-Alento" 
                className="h-24 w-auto mx-auto mb-6"
              />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-text-primary mb-6">
              T-Alento
            </h1>
            <p className="text-lg text-text-secondary mb-12 max-w-3xl mx-auto leading-relaxed">
              Un progetto di riqualificazione che unisce arte contemporanea, tradizioni locali e natura incontaminata. 
              Scopri Casal Velino attraverso le opere di Antonio Ievolella e i percorsi guidati dai Genius Loci.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/esperienze"
                className="btn-primary text-lg px-8 py-4 flex items-center justify-center space-x-2"
              >
                <span>Esplora le Esperienze</span>
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/opere"
                className="btn-secondary text-lg px-8 py-4 flex items-center justify-center space-x-2"
              >
                <Palette size={20} />
                <span>Scopri le Opere</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 bg-white">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
              Il Progetto T-Alento
            </h2>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              Un museo diffuso che trasforma Casal Velino in una galleria d'arte a cielo aperto, 
              dove ogni angolo racconta una storia e ogni opera dialoga con il territorio.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card p-8 text-center group">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-200">
                <Palette className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-text-primary mb-4">
                Arte Diffusa
              </h3>
              <p className="text-text-secondary">
                Le opere di Antonio Ievolella si integrano perfettamente nel paesaggio, 
                creando un dialogo continuo tra arte contemporanea e natura.
              </p>
            </div>

            <div className="card p-8 text-center group">
              <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-200">
                <MessageCircle className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="text-xl font-semibold text-text-primary mb-4">
                Genius Loci
              </h3>
              <p className="text-text-secondary">
                Guide virtuali che ti accompagnano attraverso storie, tradizioni e segreti 
                del territorio, rendendo ogni visita un'esperienza unica.
              </p>
            </div>

            <div className="card p-8 text-center group">
              <div className="w-16 h-16 bg-tertiary/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-200">
                <Mountain className="w-8 h-8 text-tertiary" />
              </div>
              <h3 className="text-xl font-semibold text-text-primary mb-4">
                Sentieri del Cilento
              </h3>
              <p className="text-text-secondary">
                Percorsi escursionistici che ti portano alla scoperta dei paesaggi più belli 
                del Cilento, tra mare, colline e montagne.
              </p>
            </div>

            <div className="card p-8 text-center group">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-200">
                <Store className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-xl font-semibold text-text-primary mb-4">
                Produttori Locali
              </h3>
              <p className="text-text-secondary">
                Scopri le aziende agricole, i caseifici, le cantine e i laboratori artigianali 
                che custodiscono le tradizioni del territorio.
              </p>
            </div>

            <div className="card p-8 text-center group">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-200">
                <Calendar className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-text-primary mb-4">
                Eventi e Manifestazioni
              </h3>
              <p className="text-text-secondary">
                Partecipa alle feste tradizionali, alle sagre e agli eventi culturali 
                che animano la vita del borgo durante tutto l'anno.
              </p>
            </div>

            <div className="card p-8 text-center group">
              <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-200">
                <MapPin className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="text-xl font-semibold text-text-primary mb-4">
                Mappa Interattiva
              </h3>
              <p className="text-text-secondary">
                Naviga tra i punti di interesse, le opere d'arte e i luoghi storici 
                attraverso una mappa interattiva e coinvolgente.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Project Info Section */}
      <div className="py-20 bg-gray-50">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-8">
              Un Progetto di Riqualificazione
            </h2>
            <div className="prose prose-lg mx-auto text-text-secondary">
              <p className="text-lg leading-relaxed mb-6">
                <strong>T-Alento</strong> è il nome del programma di riqualificazione di Casal Velino che prevede 
                la nascita di un Museo, un Itinerario etnoantropologico e una galleria d'arte diffusa, 
                finanziato nell'ambito del Programma di Azione e Coesione complementare al PON Cultura Sviluppo (FESR) 2014/2021.
              </p>
              <p className="text-lg leading-relaxed mb-6">
                L'ecosistema digitale (app mobile e webapp) è stato realizzato da{' '}
                <a
                  href={PROJECT_CREATOR.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-primary hover:text-secondary transition-colors duration-200"
                >
                  {PROJECT_CREATOR.name} ({PROJECT_CREATOR.websiteLabel})
                </a>
                , team di sviluppatori con sede a Benevento, e si propone di valorizzare il patrimonio culturale e 
                naturalistico del territorio attraverso l'arte contemporanea e le tecnologie digitali innovative.
              </p>
              <p className="text-lg leading-relaxed">
                Piano Operativo Cultura e Turismo (FSC) 2014/2021 – Avviso Pubblico relativo alla selezione di interventi 
                finalizzati alla riqualificazione e valorizzazione turistico-culturale dei comuni delle regioni 
                Basilicata, Calabria, Campania, Puglia, Sicilia.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-primary text-white">
        <div className="container-max text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Inizia la Tua Avventura
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Scarica l'app T-Alento e scopri Casal Velino come non l'hai mai vista prima. 
            Un'esperienza unica tra arte, natura e tradizioni.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/esperienze"
              className="bg-white text-primary px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <Activity size={20} />
              <span>Esplora Ora</span>
            </Link>
            <Link
              to="/chi-siamo"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-primary transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <Users size={20} />
              <span>Chi Siamo</span>
            </Link>
          </div>
        </div>
      </div>;

    </div>
  );
};
export default Landing;
